<template>
  <section class="page">
    <div class="page__header">
      <div class="page__title">
        <SharedBreadcrumbs :breadcrumbs />

        <FaqSearch />
      </div>

      <h1 class="typography__headline1">Цифровые товары</h1>
    </div>

    <div class="page__content">
      <FaqArticlesAside :menu />

      <FaqArticlesContent>
        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="tsifrovye-tovary"
            class="typography__headline2"
          >
            Цифровые товары
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>
              Через MyDonut можно продавать не&nbsp;только физические товары, но&nbsp;и&nbsp;цифровые. Например,
              различный цифровой контент, подарочные сертификаты, абонементы или коды погашения.
            </p>

            <p>
              Такие товары при оформлении заказа не требуют адреса доставки и после покупки отправляются покупателю
              напрямую продавцом в цифровом виде.
            </p>

            <p>Специально для таких товаров мы&nbsp;предусмотрели определенные настройки.</p>
          </div>
        </div>

        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="sposob-dostavki"
            class="typography__headline2"
          >
            Способ доставки
          </FaqArticlesBlockHeading>

          <p>
            В&nbsp;меню слева перейдите в&nbsp;раздел &laquo;Данные проекта&raquo;. На&nbsp;появившейся странице
            выберите &laquo;Доставка&raquo;.
          </p>

          <FaqArticlesImageModalButton
            image-source="/images/faq/tsifrovye-tovary-1.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>По&nbsp;кнопке сверху справа добавим способ доставки.</p>

          <FaqArticlesImageModalButton
            image-source="/images/faq/tsifrovye-tovary-2.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <div class="block-small">
            <p>В&nbsp;описании способа доставки можно указать важную дополнительную информацию.</p>

            <p>
              Переключатель с&nbsp;запросом адреса доставки влияет на&nbsp;возможность покупки ваших товаров без
              указания адреса доставки на&nbsp;оформлении. Для цифровых товаров эту настройку необходимо отключить.
            </p>

            <p>Как заполните всю нужную информацию, нажмите &laquo;Добавить&raquo; в&nbsp;самом низу окна.</p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/tsifrovye-tovary-3.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />
        </div>

        <div class="base-content-block">
          <FaqArticlesBlockHeading
            id="dobavlenie-uslugi-i-zakaza"
            class="typography__headline2"
          >
            Добавление услуги и заказа
          </FaqArticlesBlockHeading>

          <div class="block-small">
            <p>После добавления нужного способа доставки, выберем его в&nbsp;подходящих товарах.</p>

            <p>
              Для этого
              <BaseLink
                class="ui-link-secondary"
                to="/faq/articles/dobavlenie-i-redaktirovanie-tovara"
                >добавьте новый товар или отредактируйте</BaseLink
              >
              уже созданные.
            </p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/tsifrovye-tovary-3.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <div class="block-small">
            <p>
              Теперь можно создавать
              <BaseLink
                class="ui-link-secondary"
                to="/faq/articles/dobavlenie-i-redaktirovanie-zakaza"
                >новые заказы</BaseLink
              >
              с&nbsp;выбранным способом доставки и&nbsp;подходящими товарами.
            </p>

            <p>В&nbsp;таких заказах у&nbsp;клиентов не&nbsp;будет запрашиваться адрес доставки.</p>
          </div>

          <FaqArticlesImageModalButton
            image-source="/images/faq/tsifrovye-tovary-4.jpg"
            @click="(imageSource) => (activeImageSource = imageSource)"
          />

          <p>
            С&nbsp;продажей цифровых товаров разобрались. Самое время перейти к&nbsp;добавлению
            <BaseLink
              class="ui-link-secondary"
              to="/faq/articles/dobavlenie-i-redaktirovanie-zakaza"
              >заказов</BaseLink
            >.
          </p>
        </div>
      </FaqArticlesContent>
    </div>

    <div class="page__related">
      <h2
        class="typography__headline2"
        v-html="t('pages.faq.otherArticles')"
      />

      <FaqArticlesList :articles="relatedArticles" />
    </div>
  </section>

  <LazyFaqArticlesImageModal
    v-if="isModalOpen(MODAL.FaqArticlesImage) && activeImageSource"
    :src="activeImageSource"
    @close="() => (activeImageSource = null)"
  />
</template>

<script lang="ts" setup>
const { t } = useI18n();
const { isModalOpen } = useModalStore();
const activeImageSource = ref<null | string>(null);

const breadcrumbs = [
  {
    name: t("pages.faq.title"),
    url: "/faq",
  },
  {
    name: "Заказы с товарами",
    url: "/faq/categories/zakazy-s-tovarami",
  },
  {
    name: "Цифровые товары",
  },
];

const menu = [
  {
    name: "Цифровые&nbsp;товары",
    url: "tsifrovye-tovary",
  },
  {
    name: "Способ&nbsp;доставки",
    url: "sposob-dostavki",
  },
  {
    name: "Добавление&nbsp;услуги&nbsp;и&nbsp;заказа",
    url: "dobavlenie-uslugi-i-zakaza",
  },
];

const relatedArticles = [
  {
    name: "Способы доставки",
    slug: "sposoby-dostavki",
  },
  {
    name: "Категории товаров",
    slug: "kategorii-tovarov",
  },
  {
    name: "Добавление и&nbsp;редактирование товара",
    slug: "dobavlenie-i-redaktirovanie-tovara",
  },
  {
    name: "Список товаров и&nbsp;их данные",
    slug: "spisok-tovarov-i-ikh-dannye",
  },
  {
    name: "Список заказов и&nbsp;их данные",
    slug: "spisok-zakazov-i-ikh-dannye",
  },
  {
    name: "Добавление и&nbsp;редактирование заказа",
    slug: "dobavlenie-i-redaktirovanie-zakaza",
  },
];

useSeoMeta({
  title: "Цифровые товары",
});
</script>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  row-gap: 40px;
}

.page__header {
  display: flex;
  flex-direction: column;
  row-gap: 40px;
}

.page__title {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
}

.page__content {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

@media (--xl) {
  .page__content {
    flex-direction: row-reverse;
    justify-content: start;
    align-items: start;
  }
}

.page__related {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
}
</style>
